import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { CommonInput } from '@/components/ui/common-input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Lock, User, Truck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  
  const { theme } = useTheme();
  const { login, isAuthenticated, userProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (isAuthenticated) {
    const userRole = userProfile?.role?.toLowerCase();
    if (userRole === 'transporter') {
      return <Navigate to="/live-tracking" replace />;
    } else if (userRole === 'distributor') {
      return <Navigate to="/trip-management" replace />;
    } else if (userRole === 'account_officer') {
      return <Navigate to="/correction-tickets" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast({
        title: "Missing Information",
        description: "Please enter both username and password",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(formData.username, formData.password);
      
      if (result.success) {
        toast({
          title: "Login Successful",
          description: "Welcome back! Redirecting...",
        });
    
      } else {
        toast({
          title: "Login Failed",
          description: result.error || "Invalid credentials. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Login Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <Card className={`border-0 bg-transparent shadow-none ${theme}`}>
        <CardHeader className="text-center space-y-4 px-0 pb-6">
          <div className="flex justify-center md:hidden mb-4">
            <img 
              src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" 
              alt="IPL VLTS Tracking Logo" 
              className="h-16 object-contain"
            />
          </div>
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <Truck className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-3xl font-bold text-foreground">
            IPL VLTS 
            </CardTitle>
            <CardDescription className="text-base">
              Sign in to your account
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="px-0">
          <form onSubmit={handleSubmit} className="space-y-5">
            <CommonInput
              id="username"
              type="text"
              label="Username"
              placeholder="Enter your username"
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              leftIcon={<User className="h-5 w-5 text-muted-foreground" />}
              disabled={loading}
              required
            />
            
            <CommonInput
              id="password"
              type={showPassword ? "text" : "password"}
              label="Password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              leftIcon={<Lock className="h-5 w-5 text-muted-foreground" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex items-center text-muted-foreground hover:text-foreground pointer-events-auto"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              }
              disabled={loading}
              required
            />

           
            <CommonButton 
              type="submit" 
              className="w-full h-12 text-base font-semibold rounded-lg"
              loading={loading}
            >
              Sign in
            </CommonButton>
          </form>
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-4 px-0 pt-6">
          <div className="text-center">
            <Link 
              to="/forgot-password" 
              className="text-sm text-primary hover:underline"
            >
              Forgot Password?
            </Link>
          </div>
          <div className="text-center pt-2">
            <a 
              href="https://play.google.com/store/apps/details?id=com.map_app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block transition-all hover:scale-105 duration-200"
            >
              <img 
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRiP-jWjq8MCE2-YxEGf5VVgGF4xlsqBye7Hg&s" 
                alt="Get it on Google Play" 
                className="h-12 object-contain mx-auto"
              />
            </a>
          </div>
          <div className="text-center text-xs text-muted-foreground/60">
            <p>© 2025 IPL VLTS Platform</p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}